"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergePresentationAwareFilters = exports.mergePresentationAwareProductFilter = exports.mergeProductPresentationAttributesFilter = exports.createFilterFromAttributeNode = exports.getCategoriesFilter = exports.createPresentationFilterFromAttributeNode = exports.createProductPresentationAttributeFilter = void 0;
const graphql_1 = require("../../../../__generated__/graphql");
const ramda_1 = require("ramda");
const attributes_1 = require("../../../constants/attributes");
/**
 * Creates the backend presentation attribute filter for strategy-aware AI-enriched labels.
 * Known values belong in `values`; true Unknown is represented by `includeUnknown` for the same label.
 */
const createProductPresentationAttributeFilter = (label, values = [], includeUnknown = false, excludedValues = []) => (Object.assign(Object.assign({ label }, (values.length || excludedValues.length) && {
    values: Object.assign(Object.assign({}, values.length && { in: values }), excludedValues.length && { notIn: excludedValues }),
}), includeUnknown && { includeUnknown }));
exports.createProductPresentationAttributeFilter = createProductPresentationAttributeFilter;
/**
 * Converts a middle-section selected attribute into the same presentation-aware filter shape used by the product table.
 * Measure attributes still use ProductFilter because the new presentation filter contract only covers text labels.
 */
const createPresentationFilterFromAttributeNode = ({ label, value, unit, }) => {
    if (unit) {
        return { product: { attributes: (0, exports.createFilterFromAttributeNode)({ label, value, unit }) } };
    }
    if (value === attributes_1.SpecialAttributeValue.Unknown) {
        return { presentationAttributes: [(0, exports.createProductPresentationAttributeFilter)(label, [], true)] };
    }
    if (value !== undefined && value !== null) {
        return { presentationAttributes: [(0, exports.createProductPresentationAttributeFilter)(label, [String(value)])] };
    }
    return null;
};
exports.createPresentationFilterFromAttributeNode = createPresentationFilterFromAttributeNode;
const getCategoriesFilter = (productTreeNodeName) => ({
    categories: {
        some: {
            category: {
                equals: productTreeNodeName,
            },
        },
    },
});
exports.getCategoriesFilter = getCategoriesFilter;
const createFilterFromAttributeNode = ({ label, value, unit }, productTreeNodeName) => {
    const filterKey = unit ? 'measures' : 'labels';
    const labelFilterKey = unit ? 'dimension' : 'label';
    const categoryFilter = productTreeNodeName ? (0, exports.getCategoriesFilter)(productTreeNodeName) : null;
    /* the meaning of the filter is:
      "Show me products where none of the label attributions have the label equal to {label} and "VALID" status."
    */
    if (value === attributes_1.SpecialAttributeValue.Unknown) {
        return [
            Object.assign({ [filterKey]: {
                    none: {
                        [labelFilterKey]: {
                            equals: label,
                        },
                        status: {
                            in: [graphql_1.ProductAttributeStatus.InStrategy, graphql_1.ProductAttributeStatus.Valid],
                        },
                    },
                } }, categoryFilter),
        ];
    }
    return [Object.assign({ [filterKey]: {
                some: Object.assign({ [labelFilterKey]: {
                        equals: label,
                    }, value: {
                        equals: value,
                    }, status: {
                        in: [graphql_1.ProductAttributeStatus.InStrategy, graphql_1.ProductAttributeStatus.Valid],
                    } }, unit ? {
                    unit: {
                        equals: unit,
                    },
                } : null),
            } }, categoryFilter)];
};
exports.createFilterFromAttributeNode = createFilterFromAttributeNode;
const mergeProductPresentationAttributesFilter = (productListProductAttributesFilter, productListProductFilter, search) => {
    if (!search && !productListProductAttributesFilter.length && (0, ramda_1.isEmpty)(productListProductFilter)) {
        return null;
    }
    return Object.assign({}, (!!productListProductAttributesFilter.length || !(0, ramda_1.isEmpty)(productListProductFilter) || search) && {
        product: Object.assign(Object.assign(Object.assign({}, productListProductFilter), !!productListProductAttributesFilter.length && { attributes: productListProductAttributesFilter }), search && { search }),
    });
};
exports.mergeProductPresentationAttributesFilter = mergeProductPresentationAttributesFilter;
/**
 * Converts the internal presentation-aware carrier to the ProductPresentationFilter accepted by product list queries.
 * This is the last step before GraphQL, so presentation filters stay top-level and search is merged into `product`.
 */
const mergePresentationAwareProductFilter = (filter, search) => {
    const { product = {}, presentationAttributes = [], presentationAttributeFilterStrategy, nodeStrategyAttributes, } = filter !== null && filter !== void 0 ? filter : {};
    if (!search && (0, ramda_1.isEmpty)(product) && !presentationAttributes.length && !(nodeStrategyAttributes === null || nodeStrategyAttributes === void 0 ? void 0 : nodeStrategyAttributes.length)) {
        return null;
    }
    return Object.assign(Object.assign(Object.assign(Object.assign({}, (!(0, ramda_1.isEmpty)(product) || search) && {
        product: Object.assign(Object.assign({}, product), search && { search }),
    }), presentationAttributes.length && { presentationAttributes }), presentationAttributes.length && presentationAttributeFilterStrategy && { presentationAttributeFilterStrategy }), (nodeStrategyAttributes === null || nodeStrategyAttributes === void 0 ? void 0 : nodeStrategyAttributes.length) && { nodeStrategyAttributes });
};
exports.mergePresentationAwareProductFilter = mergePresentationAwareProductFilter;
/**
 * Merges product-table filters with middle-section and bulk-action filters without placing presentation fields inside ProductFilter.
 * `product.attributes` are concatenated; `presentationAttributes` and `nodeStrategyAttributes` remain top-level API fields.
 */
const mergePresentationAwareFilters = (...filters) => filters.filter(Boolean).reduce((acc, filter) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const attributes = [
        ...((_b = (_a = acc.product) === null || _a === void 0 ? void 0 : _a.attributes) !== null && _b !== void 0 ? _b : []),
        ...((_d = (_c = filter.product) === null || _c === void 0 ? void 0 : _c.attributes) !== null && _d !== void 0 ? _d : []),
    ];
    const product = Object.assign(Object.assign(Object.assign({}, acc.product), filter.product), attributes.length && { attributes });
    const presentationAttributes = [
        ...((_e = acc.presentationAttributes) !== null && _e !== void 0 ? _e : []),
        ...((_f = filter.presentationAttributes) !== null && _f !== void 0 ? _f : []),
    ];
    const nodeStrategyAttributes = [
        ...((_g = acc.nodeStrategyAttributes) !== null && _g !== void 0 ? _g : []),
        ...((_h = filter.nodeStrategyAttributes) !== null && _h !== void 0 ? _h : []),
    ];
    return Object.assign(Object.assign(Object.assign(Object.assign({}, !(0, ramda_1.isEmpty)(product) && { product }), presentationAttributes.length && { presentationAttributes }), { presentationAttributeFilterStrategy: (_j = filter.presentationAttributeFilterStrategy) !== null && _j !== void 0 ? _j : acc.presentationAttributeFilterStrategy }), nodeStrategyAttributes.length && { nodeStrategyAttributes });
}, {});
exports.mergePresentationAwareFilters = mergePresentationAwareFilters;
